import { MedicineBoxOutlined, SolutionOutlined, TeamOutlined } from "@ant-design/icons";
import { ReactNode } from "react";
import { MessageKey } from "./i18n";
import { RouteNames } from "./routes";

export interface NavItem {
    /** Route segment; doubles as the item's key. */
    route: string;
    /** Catalogue key rather than a literal — the rail, the trail and the
        palette all render this, and all three have to follow the language. */
    labelKey: MessageKey;
    icon: ReactNode;
}

export interface NavGroup {
    /** The group's eyebrow label, as a catalogue key. */
    labelKey: MessageKey;
    items: NavItem[];
}

/**
 * The single description of the primary navigation.
 *
 * The sidebar renders it, the topbar reads it to name the current location, and
 * the command palette searches it — so a new section is added in one place.
 *
 * Account is deliberately absent: signing out and looking at the current
 * identity belong to the user menu in the topbar, not to a section of the app.
 * The route still exists and the palette still finds it.
 */
export const navGroups: NavGroup[] = [
    {
        labelKey: "nav.group.clinic",
        items: [
            { route: RouteNames.doctors, labelKey: "nav.doctors", icon: <MedicineBoxOutlined /> },
            { route: RouteNames.caretakers, labelKey: "nav.caretakers", icon: <SolutionOutlined /> },
            { route: RouteNames.patients, labelKey: "nav.patients", icon: <TeamOutlined /> }
        ]
    }
];

export const navItems: NavItem[] = navGroups.flatMap(group => group.items);

/** The nav entry a pathname belongs to, matched on its first segment. */
export function findNavItem(pathname: string): NavItem | undefined {
    const segment = pathname.split("/").filter(Boolean)[0];
    return navItems.find(item => item.route === segment);
}

/** The group a nav entry sits in, for the topbar trail. */
export function findNavGroup(item: NavItem | undefined): NavGroup | undefined {
    if (item == null) return undefined;
    return navGroups.find(group => group.items.includes(item));
}
